import { createApp } from 'vue';
import ReviewComponent from '@thehotelsnetwork/review-widget/dist/review.es';
import css from '@thehotelsnetwork/review-widget/dist/review.styles';
import { findMountElement, isNumeric } from './main.js';
import { createRootContainer, insertRootContainer } from './chunk-insert-root-container.js';
import './chunk-widgets.js';
import '@thehotelsnetwork/md5';

// eslint-disable-next-line import/no-extraneous-dependencies
const factory = ({ document, thn, }) => {
    const viewData = {};
    const widgetView = {
        data: viewData,
        hasFailed() {
            return !!viewData.error;
        },
        async create(ctx) {
            const { bundle, debugPrefix } = ctx;
            if (viewData.el) {
                return;
            }
            const reference = findMountElement(bundle.deviceCssSelector, { checkId: this.data.id, document });
            if (!reference) {
                const error = new Error(`Unable to find the target container "${bundle.deviceCssSelector}"`);
                error.status = 'no-selector';
                viewData.error = error; // keep a copy
                throw error;
            }
            const styleProps = {
                'background-color': 'transparent',
                border: 0,
                display: 'block',
                float: 'none',
                margin: bundle.deviceMargin || 0,
                padding: 0,
                'pointer-events': 'auto',
                position: 'relative',
                visibility: 'hidden',
                'max-width': '100%',
            };
            const { shadowEl, topElement, shadowRoot } = createRootContainer({ id: this.data.id, contentStyles: css.toString(), topElementStyles: styleProps });
            viewData.el = topElement;
            viewData.shadowRoot = shadowRoot;
            viewData.shadowEl = shadowEl;
            const zIndex = isNumeric(bundle.zIndex) ? String(Number(bundle.zIndex) + 1) : (this.data.defaultZIndex);
            const { shadowEl: shadowModal, topElement: modalTopElement, shadowRoot: modalShadowRoot, } = createRootContainer({ id: `${this.data.id}_modal`, contentStyles: css.toString(), zIndex });
            viewData.modal = modalTopElement;
            viewData.modalShadowRoot = modalShadowRoot;
            viewData.shadowModal = shadowModal;
            // do the injection in the DOM
            const isRootInserted = insertRootContainer(reference, topElement, bundle.devicePosition);
            if (!isRootInserted) {
                this.logger.warn(debugPrefix, 'Failed to inject root container in the DOM with data:', { reference, element: topElement, position: bundle.devicePosition });
                const error = new Error('Unable to insert the root container in the DOM');
                error.status = 'no-selector';
                viewData.error = error; // keep a copy
                throw error;
            }
            // attach modal to document
            document.body.appendChild(modalTopElement);
            // zIndex must be initialized negative and not visible
            topElement.setAttribute('data-thn-visible', '0');
        },
        async show(ctx) {
            let effectiveEl = viewData.shadowEl || viewData.el;
            if (!effectiveEl) {
                await widgetView.create.call(this, ctx);
                effectiveEl = viewData.shadowEl || viewData.el;
            }
            if (!effectiveEl || !viewData.el) {
                return;
            }
            const { bundle } = ctx;
            // If an status error was produced by this view itself, then pick it.
            // This is useful to override a backend "widget-ok" status, with the
            // corrected error status produced in the front.
            if (viewData.error && viewData.error.status) {
                this.data.status = viewData.error.status;
                this.data.showed = false;
                this.remove?.(false); // keep data
                return;
            }
            // If already created, unmount before recreate
            if (!thn.empty(viewData.app)) {
                viewData.app.unmount();
            }
            // TODO: make sure this goes into the Widget level once the iframe views are deprecated
            this.data.status = 'widget-ok';
            this.data.showed = true;
            bundle.modalRootContainer = viewData.shadowModal || viewData.modal;
            await this.container.scheduler.yield();
            viewData.app = createApp(ReviewComponent, { config: bundle });
            viewData.vm = viewData.app.mount(effectiveEl);
            // Make it visible
            viewData.el.style.visibility = 'visible';
            viewData.el.setAttribute('data-thn-visible', '1');
            const zIndex = Number(viewData.el.style.zIndex);
            if (zIndex < 0) {
                viewData.el.style.zIndex = String(zIndex * -1);
            }
        },
        destroy() {
            if (viewData.app) {
                viewData.app?.unmount();
                delete viewData.app;
                delete viewData.vm;
            }
            if (viewData.el) {
                viewData.el?.remove();
                delete viewData.el;
            }
            if (viewData.modal) {
                viewData.modal?.remove();
                delete viewData.modal;
            }
        },
    };
    return widgetView;
};

export { factory };
